import React, { createContext, useContext, useMemo, useState } from 'react';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { MOCK_BRANDING } from '../config/mockBranding';

const BrandingContext = createContext(null);

/**
 * Full institution label (prefers legal name).
 * @param {{ name?: string, shortName?: string, productName?: string }} branding
 * @returns {string}
 */
export function getDisplayName(branding) {
  const n = branding?.name?.trim();
  const s = branding?.shortName?.trim();
  const p = branding?.productName?.trim() || 'StackTrack';
  return n || s || p;
}

/**
 * Compact label for nav / brand row (prefers shortName).
 * @param {{ name?: string, shortName?: string, productName?: string }} branding
 * @returns {string}
 */
export function getDisplayShortName(branding) {
  const s = branding?.shortName?.trim();
  const n = branding?.name?.trim();
  const p = branding?.productName?.trim() || 'StackTrack';
  return s || n || p;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return ctx;
}

function BrandingThemeMerge({ children }) {
  const outerTheme = useTheme();
  const { branding } = useBranding();
  const mergedTheme = useMemo(() => {
    const c = branding?.primaryColor?.trim();
    if (!c) return outerTheme;
    return createTheme(outerTheme, {
      palette: { primary: { main: c } },
    });
  }, [outerTheme, branding?.primaryColor]);

  return <ThemeProvider theme={mergedTheme}>{children}</ThemeProvider>;
}

export function BrandingProvider({ children, initialBranding = MOCK_BRANDING }) {
  const [branding, setBranding] = useState(() => ({
    name: initialBranding?.name ?? '',
    shortName: initialBranding?.shortName ?? '',
    logoUrl: initialBranding?.logoUrl ?? '',
    primaryColor: initialBranding?.primaryColor,
    productName: initialBranding?.productName ?? 'StackTrack',
  }));

  const value = useMemo(
    () => ({
      branding,
      setBranding,
      displayName: getDisplayName(branding),
      displayShortName: getDisplayShortName(branding),
      productName: branding?.productName?.trim() || 'StackTrack',
      hasLogo: Boolean(branding?.logoUrl?.trim()),
      /** True when a non-empty institution `name` is configured (not the StackTrack fallback). */
      hasCustomInstitutionName: Boolean(branding?.name?.trim() || branding?.shortName?.trim()),
    }),
    [branding],
  );

  return (
    <BrandingContext.Provider value={value}>
      <BrandingThemeMerge>{children}</BrandingThemeMerge>
    </BrandingContext.Provider>
  );
}

export default BrandingContext;
